/**
 * SFrame134 – IAD Step 2: Secure the Residence (hub)
 * Standardized Checklist UX matching ticket specifications.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProgressHeader from '../settlement/ProgressHeader';
import DocumentUploadSection from '../settlement/DocumentUploadSection';
import ChecklistItem from '../settlement/ChecklistItem';
import { useSettlement } from '../../contexts/SettlementContext';
import { useSettlementSession } from '../../hooks/useSettlementSession';

// Standardized Logic & Types matched exactly to Frame 124
import { documentService } from '../../services/documentService';
import {
  deleteSettlementDocument,
  listSettlementDocuments,
  uploadSettlementDocument,
} from '../../utils/settlementDocuments';
import type { AppDocument } from '../../types/document';

import {
  loadResidencePanelCompletion,
  withEstatePath,
} from '../../utils/secureResidenceFlow';
import { SETTLEMENT_PLANNER_LAYOUT } from '../settlement/settlementTheme';

const PHASE = 'iad';
const STEP_ID = 'iad-2';
const OVERVIEW_ROUTE = '/sframe-141';
const isPlanner = true;

const containerClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.container : '';
const contentColumnClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.contentColumn : '';
const titleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.title : 'font-display text-[28px] leading-tight text-blue-900 capitalize';
const subtitleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.subtitle : 'text-sm text-grey-600 mb-6 leading-relaxed';

const RESIDENCE_SECURE_PANELS = [
  { frameId: 'frame-residence-physical', title: 'Secure Entry Points', intro: 'Lock down physical access to the home. Document who has keys and confirm every exterior door and window is secured.' },
  { frameId: 'frame-residence-utilities', title: 'Utilities Management', intro: 'Prevent damage and unnecessary charges by locating utilities and deciding what to pause, transfer, or leave active.' },
  { frameId: 'frame-residence-mail', title: 'Mail Forwarding', intro: 'Redirect postal mail so bills, legal notices, and tax documents reach the executor instead of piling up.' },
  { frameId: 'frame-residence-insurance', title: 'Property Insurance', intro: 'Confirm the home remains covered and that the insurer knows occupancy may have changed.' },
  { frameId: 'frame-residence-alarm', title: 'Security System', intro: 'Transfer or disable alarms, cameras, and smart devices so the property stays monitored appropriately.' },
  { frameId: 'frame-residence-final', title: 'Residence Completion Checklist', intro: 'Final pass before leaving the home secure: valuables, perishables, pets, and documentation.' },
];

const CHECKLIST_GUIDANCE = [
  {
    what: 'Make sure the home is physically secure.',
    why: 'Prevents unauthorized access, theft, or liability.',
    actions: ['Locate all keys, garage openers, and access codes', 'Change locks or reset entry codes if needed', 'Confirm all doors and windows are locked', 'Document who currently has access'],
  },
  {
    what: 'Review and manage all home utilities.',
    why: 'Avoids damage (pipes, mold) and unnecessary charges.',
    actions: ['Identify all utilities (electric, gas, water, internet)', 'Keep essential services on (heat, electricity)', 'Pause or cancel non-essential services', 'Monitor for unusual usage or outages'],
  },
  {
    what: 'Redirect mail to the executor.',
    why: 'Ensures bills, legal notices, and financial documents aren’t missed.',
    actions: ['Set up mail forwarding with USPS (online or in person)', 'Update mailing address with key institutions (banks, insurance)', 'Monitor incoming mail for accounts or obligations', 'Keep important documents organized'],
  },
  {
    what: 'Confirm and update homeowners insurance.',
    why: 'Vacant homes may not be covered under standard policies.',
    actions: ['Notify insurer of the death and property status', 'Confirm coverage remains active', 'Ask about vacant home or estate coverage', 'Document policy details and contact info'],
  },
  {
    what: 'Manage alarms, cameras, and monitoring systems.',
    why: 'Ensures continued protection and avoids service disruptions.',
    actions: ['Locate alarm provider and account details', 'Update contact and emergency notification info', 'Transfer or maintain monitoring service', 'Test system functionality'],
  },
  {
    what: 'Final pass to ensure nothing is missed.',
    why: 'Confirms the home is secure and ready for next steps (sale, transfer, etc.).',
    actions: ['Remove perishables and dispose of trash', 'Secure valuables or document their location', 'Check appliances, water, and thermostat settings', 'Take photos to document condition'],
  },
];

export default function SFrame134() {
  const { estateId: paramEstateId } = useParams<{ estateId: string }>();
  const estateId = paramEstateId || null;
  const navigate = useNavigate();
  const { isArchived } = useSettlement();
  const readOnly = isArchived;
  const { error, loadFrameData, loadPhaseProgress, updateStepProgress, saveFrameData } = useSettlementSession(estateId, {
    readOnly,
  });

  const [completedPanels, setCompletedPanels] = useState(0);
  const [panelDone, setPanelDone] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState('');
  const [documents, setDocuments] = useState<AppDocument[]>([]);

  // MATCHED TO FRAME 124: Single, holistic refresh handler for data sync operations
  const refresh = useCallback(async () => {
    if (!estateId) return;

    // 1. Load panel items completion status maps
    const { completedCount, byFrameId } = await loadResidencePanelCompletion(estateId, loadFrameData);
    setCompletedPanels(completedCount);
    setPanelDone(byFrameId);

    // 2. Load and normalize attachments directly into flat state arrays
    try {
      const rows = await listSettlementDocuments(estateId, PHASE, STEP_ID);
      setDocuments(documentService.normalizeMany(rows));
    } catch (e) {
      console.error('[SFrame134] Failed to sync document lists', e);
    }

    // 3. Load workflow step notes
    try {
      const steps = await loadPhaseProgress(PHASE);
      const row = steps.find((s) => s.stepId === STEP_ID);
      if (row?.notes) {
        setNotes(row.notes);
      }
    } catch (e) {
      console.error('[SFrame134] Failed to sync session notes', e);
    }
  }, [estateId, loadFrameData, loadPhaseProgress]);

  // Combined single monitor hook match exactly to Frame 124 layout architecture
  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleToggleComplete = async (frameId: string, newState: boolean) => {
    if (readOnly) return;
    
    const newPanelDone = { ...panelDone, [frameId]: newState };
    setPanelDone(newPanelDone);
    setCompletedPanels(Object.values(newPanelDone).filter(Boolean).length);

    if (estateId) {
      await saveFrameData(frameId, { completed: newState });
      await refresh();
      const steps = await loadPhaseProgress(PHASE);
      const row = steps.find((s) => s.stepId === STEP_ID);
      if (row?.status === 'not-started' || !row) {
        await updateStepProgress(STEP_ID, PHASE, { status: 'in-progress' });
      }
    }
  };

  const handleNoteChange = async (val: string) => {
    setNotes(val);
    if (estateId && !readOnly) {
      await updateStepProgress(STEP_ID, PHASE, { notes: val });
    }
  };

  const handleDocUpload = async (files: File[]) => {
    if (!files.length || !estateId || readOnly) return;

    // 1. OPTIMISTIC UPDATE: Appends items instantly to match standard user experience pipelines
    const tempDocs = files.map(file => ({
      id: `temp-${Date.now()}-${Math.random()}`,
      name: file.name,
      type: file.type,
      size: file.size,
    }));

    const normalizedTemp = documentService.normalizeMany(tempDocs);
    setDocuments(prev => [...prev, ...normalizedTemp]);

    // 2. SERVER PERSISTENCE
    try {
      const steps = await loadPhaseProgress(PHASE);
      const row = steps.find((s) => s.stepId === STEP_ID);
      if (row?.status === 'not-started' || !row) {
        await updateStepProgress(STEP_ID, PHASE, { status: 'in-progress' });
      }

      await Promise.all(
        files.map(file =>
          uploadSettlementDocument({
            estateId,
            phase: PHASE,
            stepId: STEP_ID,
            file,
          })
        )
      );

      // Explicit refresh sync to cleanly overwrite local mock values with database records
      await refresh();
    } catch (err) {
      console.error("[SFrame134] Batch upload process failed", err);
    }
  };

  const handleDocDelete = async (docId: string) => {
    if (!estateId || readOnly) return;
    if (!confirm('Are you sure you want to remove this document?')) return;

    setDocuments(prev => prev.filter(d => d.id !== docId));

    if (!docId.toString().startsWith('temp-')) {
      try {
        await deleteSettlementDocument(estateId, docId);
        await refresh();
      } catch (err) {
        console.error("[SFrame134] Delete validation request failed", err);
      }
    }
  };

  return (
    <div className={containerClassName}>
      <div className={contentColumnClassName}>
        {error && (
          <div className="mb-4 bg-orange-100 border border-orange-300 rounded-md p-3 text-orange-900 text-sm max-w-3xl mx-auto">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => navigate(withEstatePath(OVERVIEW_ROUTE, estateId))}
            className="w-[35px] h-[35px] flex items-center justify-center text-grey-600 hover:text-blue-900 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className={titleClassName}>Secure The Residence</h1>
        </div>

        <p className={subtitleClassName}>
          Use this checklist to protect the home, prevent damage or theft, and keep essential services handled.
        </p>

        <ProgressHeader
          title="Secure the residence"
          variant={isPlanner ? 'planner' : 'default'}
          subtitle="Open each item for guidance and mark tasks complete as you go."
          completedSteps={completedPanels}
          totalSteps={RESIDENCE_SECURE_PANELS.length}
          estimatedMinutes={60}
        />

        <div className="space-y-4 mt-8">
          {RESIDENCE_SECURE_PANELS.map((panel, index) => {
            const guidance = CHECKLIST_GUIDANCE[index];
            return (
              <ChecklistItem
                key={panel.frameId}
                id={panel.frameId}
                title={panel.title}
                description={panel.intro}
                whatThisIs={guidance.what}
                whyItMatters={guidance.why}
                bulletPoints={guidance.actions}
                checked={!!panelDone[panel.frameId]}
                onChange={handleToggleComplete}
                disabled={readOnly}
              />
            );
          })}
        </div>

        {/* --- Documents Section --- */}
        <div className="mt-12 pt-10 border-t border-stone-200">
          <DocumentUploadSection
            documents={documents}
            onUpload={handleDocUpload}
            onDelete={estateId && !readOnly ? handleDocDelete : undefined}
            disabled={!estateId || readOnly}
            className="mb-8"
          />
        </div>
 
        {/* --- Notes Section --- */}
        <div className="mt-10 pt-10 border-t border-stone-200">
          <h3 className="text-lg font-semibold text-stone-900 mb-2">Notes</h3>
          <textarea
            className="w-full rounded-lg border border-stone-300 p-4 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all min-h-[120px] disabled:opacity-60"
            placeholder="Add details, locations, or reminders to keep track of your progress here"
            value={notes}
            onChange={(e) => handleNoteChange(e.target.value)}
            disabled={!estateId || readOnly}
          />
        </div>

      </div>
    </div>
  );
}